import { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectItem } from 'components/select/types';
import { selectPredicate } from 'components/select/util';
import { UseComboboxProps, useCombobox } from 'downshift';
import Fuse from 'fuse.js';

interface UseSearchableSelectProps<T, Item extends SelectItem<T>> {
    items?: Item[];
    multi?: boolean;
    clearAfterSelect?: boolean;
    localSearch?: boolean;
    selectedItems?: Item[];
    defaultItems?: Item[];
    defaultSelectedItems?: Item[];
    onSelectItem?: (selectedItem: Item | null, selectedItems: Item[], items: Item[]) => void;
    isSearchableById?: boolean;
    alwaysVisibleItemIds?: string[];
    restoreOnBlur?: boolean;
    searchOnOpen?: boolean;
    onBlur?: () => void;
    onInputChange?: (inputValue: string) => void;
}

const useSearchableSelect = <T, Item extends SelectItem<T>>(
    props: UseSearchableSelectProps<T, Item>,
) => {
    const {
        items: itemsConsumer,
        multi = false,
        clearAfterSelect = true,
        isSearchableById = false,
        alwaysVisibleItemIds = [],
        restoreOnBlur = true,
        localSearch = true,
        searchOnOpen = true,
        selectedItems: selectedItemsConsumer,
        defaultItems,
        defaultSelectedItems,
        onSelectItem,
        onBlur,
        onInputChange,
    } = props;
    const initialInputItems = useMemo(
        () => defaultItems ?? itemsConsumer ?? [],
        [defaultItems, itemsConsumer],
    );
    const searchItems = useFuzzySearch(initialInputItems, {
        isSearchableById,
        alwaysVisibleItemIds,
    });
    const initialSelectedItems = defaultSelectedItems ?? selectedItemsConsumer ?? [];
    const [inputItems, setInputItems] = useState(initialInputItems);
    const [selectedItems, setSelectedItems] = useState(initialSelectedItems);

    const getMultiProps = () => {
        const multiProps: Omit<UseComboboxProps<Item>, 'items'> = {};

        if (multi) {
            multiProps.selectedItem = null;
            multiProps.stateReducer = (state, actionAndChanges) => {
                const { changes, type } = actionAndChanges;

                switch (type) {
                    case useCombobox.stateChangeTypes.InputKeyDownEnter:
                    case useCombobox.stateChangeTypes.ItemClick:
                        return {
                            ...changes,
                            isOpen: true,
                            highlightedIndex: state.highlightedIndex,
                            inputValue: clearAfterSelect ? '' : state.inputValue,
                        };
                    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                        return {
                            ...changes,
                            inputValue: clearAfterSelect ? '' : state.inputValue,
                        };
                    case useCombobox.stateChangeTypes.InputBlur:
                        onBlur && onBlur();

                        return {
                            ...changes,
                            inputValue: '',
                        };
                    default:
                        return changes;
                }
            };
        } else {
            multiProps.onStateChange = changes => {
                if (
                    changes.type === useCombobox.stateChangeTypes.ItemClick &&
                    !changes.selectedItem
                ) {
                    const nextItem = inputItems.find(item => {
                        const label = typeof item === 'string' ? item : item.label;

                        return label === changes.inputValue;
                    });

                    if (nextItem) {
                        toggleSelectItem(nextItem);
                    }
                }
            };
            multiProps.stateReducer = (state, actionAndChanges) => {
                const { changes, type } = actionAndChanges;

                switch (type) {
                    case useCombobox.stateChangeTypes.FunctionCloseMenu:
                    case useCombobox.stateChangeTypes.InputBlur: {
                        const selectedLabel =
                            typeof state.selectedItem === 'string'
                                ? state.selectedItem
                                : state.selectedItem?.label;

                        const nextValue = state.selectedItem ? selectedLabel : state.inputValue;

                        onBlur && onBlur();

                        return {
                            ...changes,
                            inputValue: restoreOnBlur ? nextValue : state.inputValue,
                        };
                    }
                    default:
                        return changes;
                }
            };
        }

        return multiProps;
    };

    const onIsOpenChange = () => {
        if (!searchOnOpen) {
            setInputItems(initialInputItems);
        }
    };

    const comboboxProps = useCombobox<Item>({
        items: inputItems,
        itemToString: item => {
            if (!item) {
                return 'Non-existent item';
            }

            if (typeof item === 'string') {
                return item;
            }

            return item.label;
        },
        defaultSelectedItem: initialSelectedItems.length > 0 ? initialSelectedItems[0] : null,
        onInputValueChange: ({ inputValue = '' }) => {
            let nextItems: Item[] = inputItems;

            if (localSearch) {
                nextItems = searchItems(inputValue);
            }

            if (inputValue.length === 0) {
                nextItems = initialInputItems;
            }

            setInputItems(nextItems);

            onInputChange && onInputChange(inputValue);
        },
        onSelectedItemChange: ({ selectedItem, type }) => {
            if (type === useCombobox.stateChangeTypes.InputBlur) {
                return;
            }
            toggleSelectItem(selectedItem);
        },
        onIsOpenChange,
        ...getMultiProps(),
    });

    useEffect(() => {
        if (itemsConsumer) {
            setInputItems(itemsConsumer);
        }
    }, [itemsConsumer]);

    useEffect(() => {
        if (selectedItemsConsumer) {
            setSelectedItems(selectedItemsConsumer);
        }
    }, [selectedItemsConsumer]);

    const selectAll = () => {
        const nextSelectedItems = initialInputItems;
        setSelectedItems(initialInputItems);

        onSelectItem && onSelectItem(null, nextSelectedItems, inputItems);
    };

    const deselectAll = () => {
        const nextSelectedItems: Item[] = [];
        setSelectedItems(nextSelectedItems);

        onSelectItem && onSelectItem(null, nextSelectedItems, inputItems);
    };

    const toggleSelectItem = (item?: Item | null) => {
        if (!item) {
            return;
        }

        let nextSelectedItems: Item[] | undefined;

        if (multi) {
            nextSelectedItems = multiSelect(item);
        } else {
            nextSelectedItems = singleSelect(item);
        }

        setSelectedItems(nextSelectedItems);
        onSelectItem && onSelectItem(item, nextSelectedItems, inputItems);
    };

    const multiSelect = (item: Item) => {
        const curriedPredicate = selectPredicate(item);
        const selectedIndex = selectedItems.findIndex(curriedPredicate);

        let nextSelectedItems = [...selectedItems, item];

        if (selectedIndex !== -1) {
            const selectedItemsCopy = [...selectedItems];
            selectedItemsCopy.splice(selectedIndex, 1);
            nextSelectedItems = selectedItemsCopy;
        }

        return nextSelectedItems;
    };

    const singleSelect = (item: Item) => {
        return [item];
    };

    return {
        items: inputItems,
        selectedItems,
        toggleSelectItem,
        selectAll,
        deselectAll,
        ...comboboxProps,
    };
};

const useFuzzySearch = <T, Item extends SelectItem<T>>(
    items: Item[],
    options: { isSearchableById?: boolean; alwaysVisibleItemIds?: string[] } = {},
) => {
    const { isSearchableById = false, alwaysVisibleItemIds = [] } = options;

    const itemsIndex = useMemo(() => {
        const searchableKeys = ['label'];
        if (isSearchableById) {
            searchableKeys.push('id');
        }

        return new Fuse(items, {
            sortFn: (a, b) => {
                if (a.score > b.score) {
                    return 1;
                }

                return -1;
            },
            keys: items.some(item => typeof item === 'string') ? [] : searchableKeys,
            threshold: 0.3,
        });
    }, [items, isSearchableById]);

    return useCallback(
        (inputValue: string) => {
            const foundItems = itemsIndex.search(inputValue).map(found => found.item);
            const stableItems = items.filter(item =>
                alwaysVisibleItemIds.includes(typeof item === 'string' ? item : String(item.id)),
            );
            return [...stableItems, ...foundItems];
        },
        [itemsIndex, items, alwaysVisibleItemIds],
    );
};

export { useSearchableSelect, useFuzzySearch };
export type { UseSearchableSelectProps };
